import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import { WarningAmber as DiffersIcon } from '@mui/icons-material';
import type { RecordItem } from './BusinessMapTab';

// See api/DTOs/RecordDto.cs (RecordImportRowDto).
export interface RecordImportRow {
  rowId: string;
  importBatchId: string;
  fileName: string;
  formatKey: string;
  uploadedAtUtc: string;
  sourceRowKey: string | null;
  groupKey: string | null;
  businessName: string | null;
  farmName: string | null;
  fieldName: string | null;
  regionName: string | null;
  seasonName: string | null;
  productName: string | null;
  productCategoryName: string | null;
  geneStack: string | null;
  rate: number | null;
  unitName: string | null;
  areaHectares: number | null;
  bandPercent: number | null;
  irrigationType: string | null;
  operationTypeHint: string | null;
  eventDate: string | null;
  pests: string | null;
  matchedProductId: string | null;
  matchedProductGroupId: string | null;
  rawColumns: { header: string; value: string | null }[];
}

// The import row(s) a record was approved from (GET api/records/{id}/
// import-rows) — empty for a record that wasn't imported, so a caller can
// show its "Imported" tab only when there's something to show.
export const useRecordImportRows = (recordId: string | null, authHeaders: () => Record<string, string>) => {
  const [rows, setRows] = useState<RecordImportRow[]>([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    setRows([]);
    if (!recordId) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const res = await fetch(`/api/records/${recordId}/import-rows`, { headers: authHeaders() });
        if (res.ok && !cancelled) setRows(await res.json());
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [recordId, authHeaders]);
  return { rows, loading };
};

const normalize = (text: string | null | undefined) => (text ?? '').toLowerCase().replace(/[^a-z0-9]/g, '');
const formatDate = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleDateString('en-AU') : null);
const formatNumber = (n: number | null | undefined) => (n == null ? null : n.toLocaleString('en-AU', { maximumFractionDigits: 3 }));

interface Comparison {
  label: string;
  imported: string | null;
  now: string | null;
  // Flagged for a look: both sides have a value and they don't agree.
  differs: boolean;
}

const compareText = (label: string, imported: string | null, now: string | null): Comparison => ({
  label,
  imported,
  now,
  differs: !!imported && !!now && normalize(imported) !== normalize(now),
});

const compareNumber = (label: string, imported: number | null, now: number | null, suffix = ''): Comparison => ({
  label,
  imported: imported == null ? null : `${formatNumber(imported)}${suffix}`,
  now: now == null ? null : `${formatNumber(now)}${suffix}`,
  differs: imported != null && now != null && Math.abs(imported - now) > 0.01,
});

// What one import row said beside what the record says now. A tank mix is
// several rows on one record, so a row's product is compared with the line
// it was matched to (its product, else the same name), and flagged only when
// the record no longer has that line — the product was changed since. Other
// text that differs only by an alias (a gene stack's spelling) is flagged
// too — the flag means "worth a look", not "wrong".
const buildComparisons = (row: RecordImportRow, record: RecordItem | null): Comparison[] => {
  const line = record?.products.find((p) =>
    (row.matchedProductId && p.productId === row.matchedProductId)
    || (row.matchedProductGroupId && p.productGroupId === row.matchedProductGroupId))
    ?? record?.products.find((p) => normalize(p.productName) === normalize(row.productName))
    ?? null;
  const productsNow = record ? record.products.map((p) => p.productName).join(', ') : null;
  return [
    compareText('Farm', row.farmName, record?.farmName ?? null),
    compareText('Field', row.fieldName, record?.fieldName ?? (record?.farmName ? null : record?.spatialFeatureName ?? null)),
    compareText('Region', row.regionName, record?.linkedRegionName ?? record?.regionName ?? null),
    compareText('Date', formatDate(row.eventDate), record ? formatDate(record.timeFrom) : null),
    compareText('Operation', row.operationTypeHint, record?.operationTypeName ?? null),
    {
      label: 'Product',
      imported: row.productName,
      now: line?.productName ?? productsNow,
      differs: !!row.productName && !!record && record.products.length > 0 && !line,
    },
    compareText('Category', row.productCategoryName, null),
    {
      label: 'Rate',
      imported: row.rate == null ? null : `${formatNumber(row.rate)}${row.unitName ? ` ${row.unitName}` : ''}`,
      now: line ? `${formatNumber(line.rate)} ${line.rateUnitName}` : null,
      differs: row.rate != null && !!line && Math.abs(row.rate - line.rate) > 0.001,
    },
    compareNumber('Area', row.areaHectares, record?.areaHectares ?? record?.effectiveAreaHectares ?? null, ' ha'),
    compareNumber('Band', row.bandPercent, record?.bandPercent ?? null, '%'),
    compareText('Gene stack', row.geneStack, record?.geneStackName ?? null),
    compareText('Irrigated / Dryland', row.irrigationType, record?.irrigationTypeName ?? null),
    compareText('Pests', row.pests, null),
    compareText('Season', row.seasonName, null),
  ].filter((c) => c.imported || c.now);
};

const ImportRowCard: React.FC<{ row: RecordImportRow; record: RecordItem | null }> = ({ row, record }) => {
  const [showRaw, setShowRaw] = useState(false);
  const comparisons = buildComparisons(row, record);
  return (
    <Paper variant="outlined" sx={{ p: 1.5 }}>
      <Typography variant="subtitle2">{row.fileName}</Typography>
      <Typography variant="caption" color="text.secondary" display="block">
        {row.formatKey} · {row.sourceRowKey ?? 'row'}
        {row.groupKey && ` · activity ${row.groupKey}`} · uploaded {new Date(row.uploadedAtUtc).toLocaleDateString('en-AU')}
      </Typography>
      <Table size="small" sx={{ mt: 1 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: 140 }} />
            <TableCell>Imported as</TableCell>
            <TableCell>Record now</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {comparisons.map((c) => (
            <TableRow key={c.label} sx={c.differs ? { bgcolor: (t) => alpha(t.palette.warning.main, 0.12) } : undefined}>
              <TableCell sx={{ fontWeight: 500, whiteSpace: 'nowrap' }}>
                {c.differs && (
                  <Tooltip title="Differs from what was imported">
                    <DiffersIcon fontSize="inherit" color="warning" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                  </Tooltip>
                )}
                {c.label}
              </TableCell>
              <TableCell>{c.imported ?? '—'}</TableCell>
              <TableCell>{c.now ?? '—'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {row.rawColumns.length > 0 && (
        <>
          <Button size="small" onClick={() => setShowRaw((v) => !v)} sx={{ mt: 1 }}>
            {showRaw ? 'Hide' : 'Show'} every column in the file ({row.rawColumns.length})
          </Button>
          <Collapse in={showRaw} unmountOnExit>
            <Table size="small">
              <TableBody>
                {row.rawColumns.map((c) => (
                  <TableRow key={c.header}>
                    <TableCell sx={{ width: '40%', color: 'text.secondary' }}>{c.header}</TableCell>
                    <TableCell>{c.value || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Collapse>
        </>
      )}
    </Paper>
  );
};

// The "Imported" tab of a record's edit dialog: each import row the record
// came from, compared with the record as it stands (before unsaved edits).
const RecordImportDetails: React.FC<{ rows: RecordImportRow[]; loading?: boolean; record: RecordItem | null }> = ({ rows, loading, record }) => {
  if (loading) {
    return <Box display="flex" justifyContent="center" p={3}><CircularProgress size={24} /></Box>;
  }
  if (rows.length === 0) {
    return <Alert severity="info">This record wasn't imported.</Alert>;
  }
  return (
    <Box display="flex" flexDirection="column" gap={1.5}>
      {rows.length > 1 && (
        <Typography variant="body2" color="text.secondary">
          {rows.length} import rows make up this record (one per product of the pass).
        </Typography>
      )}
      {rows.map((row) => <ImportRowCard key={row.rowId} row={row} record={record} />)}
    </Box>
  );
};

export default RecordImportDetails;
